import { useQuery } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StateChip } from '../../components/atoms.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ResizablePanel } from '../../components/resizable-panel.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { type TestCaseListItem, type TreeProcess, catalogApi } from '../../lib/catalog-api.js';
import {
  activeFilterCount,
  filterTree,
  hasAnyFilter,
  hasTestCaseFilter,
  matchesTestCaseFilter,
  useCatalogFilter,
} from '../../lib/catalog-filter.js';
import { pickLang } from '../../lib/pick-lang.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { sourceSearchTerms } from '../../lib/source-label.js';
import { useShowSourceRef } from '../../lib/use-source-ref.js';
import type { FillTarget } from '../../lib/use-translation-fill-run.js';
import { CatalogFilterBar } from './catalog-filter-bar.js';
import { TestCaseTreeRow } from './testcase-tree-row.js';
import { TranslationFillDialog } from './translation-fill-dialog.js';
import type { Selection } from './types.js';

// Design-Spec 3.5 / Screen 3a: linker Baum (330px) Stream ▾ → Prozess (mit „in Scope"-Chip +
// test_case_count) → Testfälle (TestCaseTreeRow: ID + Quell-Label gestapelt, Titel, ReadinessChip).
// Auswahl eines Testfalls highlightet die Zeile (Orange-Tint + Inset-Ring) und ruft `onSelect`.
// Das Anlegen eines Testfalls oeffnet das Panel in der rechten Spalte (#28) statt eines Inline-Formulars.
// #143: Filterleiste (catalog-filter-bar.tsx) — Teilprojekt/Prozess filtern den Baum, Tester/Datei/
// Uebersetzungsluecke die Testfaelle; ProcessNode/WithoutProcessGroup zeigen dann die Treffer der
// vollen Liste (`filteredTestCases`) statt ihrer eigenen Abfrage.
// "+ Prozess" ist reine Guard-UI — die Berechtigung wird serverseitig erzwungen (requirePermission).
// #28/#44: Anlegen UND Bearbeiten eines Prozesses laufen seit Task 6 ueber ProcessPanel in der
// rechten Spalte (siehe catalog-page.tsx) statt ueber ein Modal — NewProcessDialog ist entfallen.

function ProcessNode({
  process,
  projectId,
  selection,
  onSelect,
  canEdit,
  showSourceRef,
  filteredTestCases,
  defaultExpanded,
}: {
  process: TreeProcess;
  projectId: string;
  selection: Selection;
  onSelect: (sel: Selection) => void;
  canEdit: boolean;
  /** #112 (A6): Admin-Schalter `show_source_ref` — aus, verschwindet die Quell-ID komplett. */
  showSourceRef: boolean;
  /** #143: Treffer des Testfall-Filters für diesen Prozess. Gesetzt → der Prozess startet
   *  aufgeklappt, zeigt diese Zeilen statt einer eigenen Abfrage und nennt die Trefferzahl statt
   *  `test_case_count`. */
  filteredTestCases?: TestCaseListItem[] | undefined;
  /** #143: Prozess-Filter gesetzt auf diesen Prozess → der Node startet aufgeklappt, damit
   *  seine Testfälle sofort sichtbar sind. */
  defaultExpanded?: boolean | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const [expanded, setExpanded] = useState(defaultExpanded || filteredTestCases !== undefined);

  const testCasesQuery = useQuery({
    queryKey: ['tcs', projectId, process.process_id],
    queryFn: () => catalogApi.listTestCases(projectId, process.process_id),
    enabled: expanded && filteredTestCases === undefined,
  });
  const rows = filteredTestCases ?? testCasesQuery.data;

  return (
    <div>
      {/* Klick klappt auf/zu, Doppelklick oder der Stift-Knopf oeffnen die Bearbeiten-Maske
          (#28/#44). Ein <button> darf keinen <button> enthalten, deshalb ist die Zeile ein <div>
          mit dem Aufklapp-<button> darin — die Tastatur erreicht das Aufklappen weiterhin ueber
          diesen Button. */}
      <div
        className="group flex w-full items-center gap-2 px-3 py-1.5 pl-6 text-left text-sm text-text-body hover:bg-[var(--bg-subtle)]"
        onDoubleClick={() => {
          if (canEdit) onSelect({ kind: 'process', processId: process.process_id });
        }}
      >
        {/* #95: ClipTooltip umschließt den fokussierbaren <button> (nicht umgekehrt) — nur so
            bubbelt dessen Fokus-Event in den ClipTooltip-Wrapper und löst die Blase auch per
            Tastatur aus (Muster aus tooltip.test.tsx). ClipTooltip übernimmt dafür die
            Flex-Rolle (min-w-0 flex-1), die vorher am <button> hing. */}
        <ClipTooltip
          text={pickLang(lang, process.name_de, process.name_en)}
          className="min-w-0 flex-1"
        >
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex w-full items-center gap-2 text-left"
          >
            <span className="text-text-muted">{expanded ? '▾' : '▸'}</span>
            {/* line-clamp-2 statt truncate: der Normalfall wird ohne Interaktion lesbar, erst
                Text jenseits zweier Zeilen bekommt die Blase (#95, Entscheidung „Umbruch UND
                Breite"). */}
            <span data-clip className="flex-1 line-clamp-2">
              {pickLang(lang, process.name_de, process.name_en)}
            </span>
            {/* #112 (A6): Quell-ID des Prozesses („WMI_09") — optisch schwaecher als der Name,
                nur lesbar. Sie ist zugleich der Suchbegriff, ueber den die frueheren
                „Prozessgruppen" gefunden werden. */}
            {showSourceRef && process.source_ref && (
              <span className="shrink-0 font-mono text-[11px] text-text-muted">
                {process.source_ref}
              </span>
            )}
          </button>
        </ClipTooltip>
        {!process.in_scope && <StateChip label={t('catalog.fields.notInConversionScope')} />}
        <span className="text-[11px] text-text-muted">
          {filteredTestCases?.length ?? process.test_case_count}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={() => onSelect({ kind: 'process', processId: process.process_id })}
            className="rounded-btn px-1 text-xs text-text-muted opacity-0 hover:text-text-body focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={t('catalog.actions.edit')}
            title={t('catalog.actions.edit')}
          >
            ✎
          </button>
        )}
      </div>
      {expanded && (
        <div className="pl-9">
          {testCasesQuery.isLoading && (
            <div className="px-2 py-1 text-xs text-text-muted">{t('catalog.loading')}</div>
          )}
          {rows?.map((tc) => {
            const selected =
              selection?.kind === 'testcase' && selection.testCaseId === tc.test_case_id;
            return (
              <TestCaseTreeRow
                key={tc.test_case_id}
                testCase={tc}
                title={pickLang(lang, tc.title_de, tc.title_en)}
                selected={selected}
                showSourceRef={showSourceRef}
                onSelect={() => onSelect({ kind: 'testcase', testCaseId: tc.test_case_id })}
              />
            );
          })}
          {rows?.length === 0 && (
            <div className="px-2 py-1 text-xs text-text-muted">{t('catalog.empty')}</div>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => onSelect({ kind: 'testcase-new', processId: process.process_id })}
              className="px-2 py-1 text-left text-xs text-brand-accent hover:underline"
            >
              + {t('catalog.actions.addTestCase')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Testfälle ohne Prozess erreicht der Baum sonst nicht (er führt über Struktur → Prozess →
// Testfall). Bewusst eine eigene Gruppe statt eines Pseudo-Prozesses: sie trägt keine
// Prozess-Bedienelemente (bearbeiten, Testfall anlegen) — hier gibt es nichts zu pflegen, die
// Testfälle sollen einen echten Prozess bekommen.
function WithoutProcessGroup({
  projectId,
  count,
  selection,
  onSelect,
  showSourceRef,
  filteredRows,
}: {
  projectId: string;
  count: number;
  selection: Selection;
  onSelect: (sel: Selection) => void;
  showSourceRef: boolean;
  /** #143: Treffer des Testfall-Filters ohne Prozess. Gesetzt → Gruppe startet aufgeklappt und
   *  zeigt nur diese Zeilen (Zahl = Trefferzahl). */
  filteredRows?: TestCaseListItem[] | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const [open, setOpen] = useState(filteredRows !== undefined);
  const testCasesQuery = useQuery({
    queryKey: ['tcs-without-process', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: open && filteredRows === undefined,
  });
  const rows = filteredRows ?? (testCasesQuery.data ?? []).filter((tc) => tc.process_id === null);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-status-warn hover:bg-[var(--bg-subtle)]"
      >
        <span>{open ? '▾' : '▸'}</span>
        <span className="flex-1 truncate">
          {t('catalog.tree.withoutProcess', { count: filteredRows?.length ?? count })}
        </span>
      </button>
      {open && (
        <div className="pl-6">
          {testCasesQuery.isLoading && (
            <div className="px-2 py-1 text-xs text-text-muted">{t('catalog.loading')}</div>
          )}
          {rows.map((tc) => {
            const selected =
              selection?.kind === 'testcase' && selection.testCaseId === tc.test_case_id;
            return (
              <TestCaseTreeRow
                key={tc.test_case_id}
                testCase={tc}
                title={pickLang(lang, tc.title_de, tc.title_en)}
                selected={selected}
                showSourceRef={showSourceRef}
                onSelect={() => onSelect({ kind: 'testcase', testCaseId: tc.test_case_id })}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function StreamGroup({
  label,
  processes,
  projectId,
  selection,
  onSelect,
  canEdit,
  showSourceRef,
  matchesByProcess,
  processFilter,
}: {
  label: string;
  processes: TreeProcess[];
  projectId: string;
  selection: Selection;
  onSelect: (sel: Selection) => void;
  canEdit: boolean;
  showSourceRef: boolean;
  /** #143: Treffer je Prozess bei aktivem Testfall-Filter, sonst undefined. */
  matchesByProcess?: Map<string, TestCaseListItem[]> | undefined;
  /** #143: Prozess-Filter aus der URL, um defaultExpanded zu setzen. */
  processFilter?: string | undefined;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      {/* Gruppen-Überschrift bleibt einzeilig (truncate statt line-clamp-2) — sie soll nicht
          wachsen, bekommt den ClipTooltip-Anschluss aber genau wie die Prozess-/Testfall-Zeilen. */}
      <ClipTooltip text={label} className="block w-full">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-text-primary hover:bg-[var(--bg-subtle)]"
        >
          <span>{open ? '▾' : '▸'}</span>
          <span data-clip className="truncate">
            {label}
          </span>
        </button>
      </ClipTooltip>
      {open &&
        processes.map((process) => (
          <ProcessNode
            // #143: Der Wechsel in den Filtermodus ist ein anderer Schlüssel → ProcessNode startet
            // neu und damit aufgeklappt (useState-Anfangswert), auch wenn er schon gemountet war.
            key={`${process.process_id}${matchesByProcess ? ':f' : ''}${
              processFilter === process.process_id ? ':p' : ''
            }`}
            process={process}
            projectId={projectId}
            selection={selection}
            onSelect={onSelect}
            canEdit={canEdit}
            showSourceRef={showSourceRef}
            filteredTestCases={matchesByProcess?.get(process.process_id)}
            defaultExpanded={processFilter === process.process_id}
          />
        ))}
    </div>
  );
}

// #78: eine „vernuenftige Zahl" fuer die Testfall-Trefferliste — begrenzt UND angeschrieben
// (Task-Brief: eine stille Kappung liest sich wie Vollstaendigkeit).
const TEST_CASE_HIT_LIMIT = 30;

export function ProcessTree({
  projectId,
  selection,
  onSelect,
  q = '',
}: {
  projectId: string;
  selection: Selection;
  onSelect: (sel: Selection) => void;
  /** #78: Suchbegriff aus der Katalog-Kopfzeile. Fehlt er (aeltere Aufrufer/Tests), verhaelt sich
   *  der Baum wie zuvor — ungefiltert. */
  q?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const { user } = useScopedUser(projectId);
  // I3: die Katalog-Bedienelemente hier waren clientseitig nicht an catalog.edit gebunden —
  // gleiches Muster wie step-table.tsx/glossary-tab.tsx: die Berechtigung wird serverseitig
  // erzwungen (requirePermission), aber ein Betrachter soll kein Bedienelement sehen, das im
  // 403 endet.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  // #112 (A6): ein Schalter fuer alle Anzeigeorte — steht er aus, verschwindet die Quell-ID hier
  // UND in der Testplan-Auswahl. Die SUCHE bleibt davon unberuehrt (siehe matchesQuery unten):
  // wer „WMI_" tippt, meint die Gruppe, auch wenn die ID gerade nicht angezeigt wird.
  const showSourceRef = useShowSourceRef(projectId);

  const { data: tree, isLoading } = useQuery({
    queryKey: ['catalog-tree', projectId],
    queryFn: () => catalogApi.tree(projectId),
  });

  // #143: Filterzustand liegt in der URL (f_*). Ist beim Laden schon ein Filter aktiv (Deeplink,
  // Reload), bleibt der Bereich offen — sonst wirkte der Baum ohne erkennbaren Grund gekürzt.
  const [filter, setFilter, resetFilter] = useCatalogFilter();
  const anyFilter = hasAnyFilter(filter);
  const testCaseFilter = hasTestCaseFilter(filter);
  const structuralFilter = filter.teilprojekt !== '' || filter.process !== '';
  const [filterOpen, setFilterOpen] = useState(anyFilter);

  const trimmedQuery = q.trim();
  const searching = trimmedQuery.length > 0;
  // Nullish-Fallback statt direktem Zugriff: waehrend eines Rollouts kann eine aeltere API-Antwort
  // ohne das Feld im Cache liegen — dann soll der Baum sich wie vorher verhalten, nicht leer wirken.
  const withoutProcess = tree?.without_process ?? 0;

  // #78: die volle (ungefilterte) Testfallliste wird NUR waehrend einer aktiven Suche geladen
  // (`enabled: searching`) — sonst waere die Katalogseite fuer den Normalfall teurer als heute
  // (Task-Brief-Vorgabe). ProcessNode.testCasesQuery (oben) bleibt unveraendert an `expanded`
  // gebunden, ein eigener Query-Key verhindert eine Kollision der beiden Caches.
  // #143: dieselbe Liste speist den Testfall-Filter und die Tester-/Datei-Optionen — deshalb auch
  // bei aktivem Testfall-Filter oder geöffnetem Filterbereich, ohne zweite Volllisten-Query.
  const testCaseSearchQuery = useQuery({
    queryKey: ['tcs-search', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: searching || testCaseFilter || filterOpen,
  });
  const allTestCases = testCaseSearchQuery.data;
  // Solange die Liste für einen Testfall-Filter fehlt, wäre jeder Baum falsch (ungefiltert oder
  // leer) — dann Ladezustand bzw. Fehlerkarte statt Baum (#89: Fehler nie als Leerzustand).
  const filterLoading = testCaseFilter && testCaseSearchQuery.isLoading;
  const filterFailed = testCaseFilter && testCaseSearchQuery.isError;
  const treeReady = !isLoading && !filterLoading && !filterFailed;

  // #143: Teilprojekt/Prozess wirken strukturell auf den Baum — vor der Namenssuche.
  const structural = useMemo(
    () => (tree && structuralFilter ? filterTree(tree, filter) : tree),
    [tree, structuralFilter, filter],
  );

  // #143: Treffer des Testfall-Filters, je Prozess gruppiert. null = kein Testfall-Filter (oder
  // Liste noch nicht da).
  const matches = useMemo(() => {
    if (!testCaseFilter || !allTestCases) return null;
    const byProcess = new Map<string, TestCaseListItem[]>();
    const withoutProcessRows: TestCaseListItem[] = [];
    for (const tc of allTestCases) {
      if (!matchesTestCaseFilter(tc, filter)) continue;
      if (tc.process_id == null) {
        withoutProcessRows.push(tc);
        continue;
      }
      const list = byProcess.get(tc.process_id);
      if (list) list.push(tc);
      else byProcess.set(tc.process_id, [tc]);
    }
    return { byProcess, withoutProcess: withoutProcessRows };
  }, [testCaseFilter, allTestCases, filter]);

  // Reduziert den Baum auf Prozesse, deren Name (DE oder EN) passt (nur waehrend der Suche) und die
  // — bei aktivem Testfall-Filter — mindestens einen passenden Testfall haben. Sonst der
  // strukturell gefilterte Baum.
  const displayTree = useMemo(() => {
    if (!structural || (!searching && !matches)) return structural;
    // #112 (A6): source_ref zaehlt in der Suche mit — eine Suche nach „WMI_" liefert damit die
    // ganze frühere „Prozessgruppe". Bewusst KEIN eigener Praefix-Filter: die Freitextsuche deckt
    // es ab (ausdrueckliche Entscheidung in anforderung.md A6).
    const keep = (p: TreeProcess) =>
      (!searching || matchesQuery(trimmedQuery, p.name_de, p.name_en, p.source_ref)) &&
      (!matches || matches.byProcess.has(p.process_id));
    return {
      ...structural,
      nodes: structural.nodes
        .map((node) => ({ ...node, processes: node.processes.filter(keep) }))
        .filter((node) => node.processes.length > 0),
      unlabeled: structural.unlabeled.filter(keep),
    };
  }, [structural, searching, trimmedQuery, matches]);

  // Testfall-Treffer ueber human_id/title_de/title_en — als eigene Trefferliste (kein
  // aufgeklappter Baum dafuer, Task-Brief-Vorgabe). Begrenzt auf TEST_CASE_HIT_LIMIT, die
  // Begrenzung wird unten angeschrieben statt still zu kappen.
  const testCaseHits = useMemo(() => {
    if (!searching) return [];
    const all = allTestCases ?? [];
    // #143: Teilprojekt/Prozess beschränken die Treffer auf Testfälle der im Baum verbliebenen
    // Prozesse; Tester/Datei/Lücke wirken auf jeden Treffer.
    const allowed =
      structuralFilter && structural
        ? new Set(
            [...structural.nodes.flatMap((n) => n.processes), ...structural.unlabeled].map(
              (p) => p.process_id,
            ),
          )
        : null;
    return all.filter(
      (tc) =>
        matchesQuery(
          trimmedQuery,
          tc.human_id,
          tc.title_de,
          tc.title_en,
          // #144: auch die Ketten-ID — „DTP_2" liefert alle Glieder der Kette, obwohl sie selbst
          // keine Quell-ID tragen. Gilt unabhängig von show_source_ref.
          ...sourceSearchTerms(tc.source_ref, tc.chain_refs ?? []),
        ) &&
        matchesTestCaseFilter(tc, filter) &&
        (!allowed || (tc.process_id != null && allowed.has(tc.process_id))),
    );
  }, [searching, trimmedQuery, allTestCases, structuralFilter, structural, filter]);
  const shownHits = testCaseHits.slice(0, TEST_CASE_HIT_LIMIT);

  // #143: Testfälle ohne Prozess gehören zu keinem Teilprojekt/Prozess — bei einem dieser Filter
  // ausblenden; bei Testfall-Filter nur, wenn die Gruppe Treffer hat.
  const showWithoutGroup =
    withoutProcess > 0 && !structuralFilter && (!matches || matches.withoutProcess.length > 0);

  const noProcessMatches =
    (displayTree?.nodes.length ?? 0) === 0 && (displayTree?.unlabeled.length ?? 0) === 0;
  const noResults =
    searching &&
    !isLoading &&
    !testCaseSearchQuery.isLoading &&
    // #143: scheitert die Volliste bei aktivem Testfall-Filter, zeigt die Fehlerkarte den Zustand
    // — „keine Treffer" daneben wäre gelogen. (filterFailed = Testfall-Filter UND Ladefehler.)
    !filterFailed &&
    noProcessMatches &&
    testCaseHits.length === 0;
  // #143: Filter ohne Suche, und nichts passt — eigene Zeile (catalog.search.noResults braucht
  // einen Suchbegriff).
  const filterEmpty = anyFilter && !searching && treeReady && noProcessMatches && !showWithoutGroup;

  // #78: Die Fußzeile zählt, was der Baum GERADE zeigt — bei aktiver Suche also den gefilterten
  // Stand. Sonst stünde unter zwei sichtbaren Prozessen weiterhin „35 Prozesse · 204 Testfälle",
  // und die Zahl widerspräche dem, was direkt darüber steht.
  // #143: bei Testfall-Filter zählen die Treffer (inkl. der Gruppe ohne Prozess), sonst die
  // Testfallzahl der Prozesse.
  // #111: Dieselbe Trefferzusammenstellung liefert die Zielmenge für „Alle N ergänzen" — so ist N
  // garantiert die Zahl, die die Fußzeile nennt (kein zweiter, abweichender Filterpfad).
  const { processCount, testCaseCount, fillTargets } = useMemo(() => {
    const noTargets: FillTarget[] = [];
    if (!displayTree) return { processCount: 0, testCaseCount: 0, fillTargets: noTargets };
    const all = [...displayTree.nodes.flatMap((n) => n.processes), ...displayTree.unlabeled];
    const hits: TestCaseListItem[] = matches
      ? [
          ...all.flatMap((p) => matches.byProcess.get(p.process_id) ?? []),
          ...(showWithoutGroup ? matches.withoutProcess : []),
        ]
      : [];
    const count = matches ? hits.length : all.reduce((sum, p) => sum + p.test_case_count, 0);
    return {
      processCount: all.length,
      testCaseCount: count,
      fillTargets: hits.map((tc) => ({ test_case_id: tc.test_case_id, human_id: tc.human_id })),
    };
  }, [displayTree, matches, showWithoutGroup]);

  // #111: Sammelaktion nur für admin/testmanager und nur, wenn der Lücken-Filter die Liste auf
  // Testfälle mit fehlender Übersetzung einengt (sonst hieße „Alle N" etwas anderes).
  const canBulk = user ? canInAnyScope(user, 'catalog.translate_bulk') : false;
  // Momentaufnahme beim Start: Während des Laufs lädt der Baum neu (Invalidierung) und die Treffer
  // schrumpfen — die Zielmenge des Dialogs darf sich dadurch nicht ändern.
  const [bulkTargets, setBulkTargets] = useState<FillTarget[] | null>(null);

  const filterCount = activeFilterCount(filter);

  return (
    // #95: Breite verstellbar (Drag + Pfeiltasten) statt fest w-[330px] — ResizablePanel liefert
    // flex/shrink/border selbst, daher hier nicht doppelt setzen.
    <>
      <ResizablePanel
        storageKey="testflow.treeWidth.catalog"
        defaultWidth={380}
        minWidth={280}
        maxWidth={560}
        ariaLabel={t('catalog.tree.resize')}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
            {t('catalog.tree.title')}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((o) => !o)}
              className={`rounded-btn border px-2 py-1 text-xs hover:bg-brand-accent/10 ${
                filterCount > 0
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-[var(--border)] text-text-body'
              }`}
            >
              {filterCount > 0
                ? t('catalog.filter.toggleActive', { count: filterCount })
                : t('catalog.filter.toggle')}
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => onSelect({ kind: 'process-new' })}
                className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10"
              >
                + {t('catalog.actions.addProcess')}
              </button>
            )}
          </div>
        </div>
        {filterOpen && (
          <CatalogFilterBar
            tree={tree}
            testCases={allTestCases ?? []}
            filter={filter}
            onChange={setFilter}
            onReset={resetFilter}
            loading={testCaseSearchQuery.isLoading}
            bulkFill={
              filter.gap && canBulk && fillTargets.length > 0
                ? { count: fillTargets.length, onStart: () => setBulkTargets(fillTargets) }
                : undefined
            }
          />
        )}
        <div className="flex-1 overflow-y-auto">
          {isLoading && <div className="p-3 text-sm text-text-muted">{t('catalog.loading')}</div>}
          {!isLoading && filterLoading && (
            <div className="p-3 text-sm text-text-muted">{t('catalog.filter.loading')}</div>
          )}
          {!isLoading && filterFailed && (
            <QueryErrorNotice
              compact
              className="p-3"
              onRetry={() => void testCaseSearchQuery.refetch()}
            />
          )}

          {!isLoading && noResults && <SearchEmptyNotice query={trimmedQuery} className="p-3" />}
          {filterEmpty && (
            <div className="p-3 text-sm text-text-muted">{t('catalog.filter.empty')}</div>
          )}

          {!isLoading && searching && shownHits.length > 0 && (
            <div className="border-b border-[var(--border)] px-3 py-2">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-text-muted">
                {t('catalog.search.testCaseHitsHeading')}
              </div>
              <div className="space-y-0.5">
                {shownHits.map((tc) => {
                  const selected =
                    selection?.kind === 'testcase' && selection.testCaseId === tc.test_case_id;
                  return (
                    <TestCaseTreeRow
                      key={tc.test_case_id}
                      testCase={tc}
                      title={pickLang(lang, tc.title_de, tc.title_en)}
                      selected={selected}
                      showSourceRef={showSourceRef}
                      onSelect={() => onSelect({ kind: 'testcase', testCaseId: tc.test_case_id })}
                    />
                  );
                })}
              </div>
              {testCaseHits.length > shownHits.length && (
                <div className="mt-1 text-[11px] text-text-muted">
                  {t('catalog.search.resultsLimited', {
                    shown: shownHits.length,
                    total: testCaseHits.length,
                  })}
                </div>
              )}
            </div>
          )}

          {treeReady &&
            displayTree?.nodes.map((node) => (
              <StreamGroup
                key={node.node_id}
                label={node.name}
                processes={node.processes}
                projectId={projectId}
                selection={selection}
                onSelect={onSelect}
                canEdit={canEdit}
                showSourceRef={showSourceRef}
                matchesByProcess={matches?.byProcess}
                processFilter={filter.process}
              />
            ))}
          {treeReady && displayTree && displayTree.unlabeled.length > 0 && (
            <StreamGroup
              label={t('catalog.tree.unlabeled')}
              processes={displayTree.unlabeled}
              projectId={projectId}
              selection={selection}
              onSelect={onSelect}
              canEdit={canEdit}
              showSourceRef={showSourceRef}
              matchesByProcess={matches?.byProcess}
              processFilter={filter.process}
            />
          )}
          {/* Auch während einer Suche sichtbar: sonst wäre der eine Ort, an dem prozesslose
            Testfälle hängen, ausgerechnet beim Suchen wieder weg. */}
          {treeReady && showWithoutGroup && (
            <WithoutProcessGroup
              // #143: eigener Schlüssel im Filtermodus → startet aufgeklappt (siehe StreamGroup).
              key={matches ? 'filtered' : 'plain'}
              projectId={projectId}
              count={withoutProcess}
              selection={selection}
              onSelect={onSelect}
              showSourceRef={showSourceRef}
              filteredRows={matches?.withoutProcess}
            />
          )}
          {treeReady &&
            !searching &&
            !anyFilter &&
            tree &&
            tree.nodes.length === 0 &&
            tree.unlabeled.length === 0 &&
            withoutProcess === 0 && (
              <div className="p-3 text-sm text-text-muted">{t('catalog.empty')}</div>
            )}
        </div>
        <div className="border-t border-[var(--border)] px-3 py-2 text-[11px] text-text-muted">
          {/* Während einer Suche zählt die Fußzeile den gefilterten Baum — und sagt das auch. Ohne
            den Zusatz stünde neben einem sichtbaren Testfall-Treffer „0 Testfälle", weil der
            Treffer in der Liste darüber hängt und nicht im Baum. */}
          {/* M1 (#80-Nachschärfung): #78 macht „genau ein Treffer" beim gefilterten Baum zum
            Normalfall — vorher stand dort immer "1 Prozesse · 1 Testfälle". Jede Zahl wird jetzt
            EINZELN ueber ihr eigenes _one/_other-Paar pluralisiert und erst danach zur Fusszeile
            zusammengesetzt (ein einzelner i18next-`count` kann nicht zwei Substantive gleichzeitig
            pluralisieren). */}
          {t(searching || anyFilter ? 'catalog.tree.footerFiltered' : 'catalog.tree.footer', {
            processes: t('catalog.tree.footerProcesses', { count: processCount }),
            testCases: t('catalog.tree.footerTestCases', { count: testCaseCount }),
          })}
        </div>
      </ResizablePanel>
      {bulkTargets && (
        <TranslationFillDialog
          projectId={projectId}
          targets={bulkTargets}
          onClose={() => setBulkTargets(null)}
          // Öffnet den Testfall und schließt den Dialog — der Lauf ist dann ohnehin beendet.
          onOpenTestCase={(testCaseId) => {
            onSelect({ kind: 'testcase', testCaseId });
            setBulkTargets(null);
          }}
        />
      )}
    </>
  );
}
